import type { ColumnsType } from 'antd/es/table';
import { COLOR_OPTIONS } from 'config/style';
import dayjs, { type Dayjs } from 'dayjs';
import type { Feature, Point } from 'geojson';
import type { PointOptionsObject, SeriesLineOptions } from 'highcharts';
import type { TFunction } from 'i18next';
import { getCurrentCalendarDate } from '@features/regional-overview/utils/current-calendar-date.utils';
import { StatusTypeEnum } from '@features/regional-overview/digital-traps/components/status-tag/status-tag.model';
import type { DailyInsectCounts, RegionalOverviewDigitalTrapsProperties } from 'querys/regional-overview/regional-overview.query.models';
import StatusTag from '../components/status-tag/status-tag.component';
import type { DynamicTrapRow, TableTrapGroup, TrapGroup } from '../domain';
import { formatMonthDay } from './localized-date.utils';
import { calculateRiskLevelFromThreshold, mapDailyCountsKeys } from './trap-pest-pressure.utils';

interface GenerateDateColumnsAndKeysParams {
  selectedDate: Dayjs;
  showTodayText: boolean;
  numberOfDays?: number;
  t: TFunction;
}
export const generateDateColumnsAndKeys = ({
  selectedDate,
  showTodayText,
  numberOfDays = 7,
  t
}: GenerateDateColumnsAndKeysParams): { dateKeys: string[]; dateTableColumns: ColumnsType<DynamicTrapRow> } => {
  const today = getCurrentCalendarDate();
  const yesterday = today.subtract(1, 'day');
  const effectiveSelectedDate = selectedDate.isAfter(yesterday, 'day') ? yesterday : selectedDate;
  const dateKeys: string[] = [];
  const dateTableColumns: ColumnsType<DynamicTrapRow> = [];
  for (let i = numberOfDays - 1; i >= 0; i--) {
    const date = effectiveSelectedDate.subtract(i, 'day');
    const dateKey = date.format('YYYY-MM-DD');
    const showToday = date.isSame(today, 'day') && showTodayText;
    const displayDate = showToday ? t('pages.regional_overview.digital_traps.today') : formatMonthDay(date);
    dateKeys.push(dateKey);
    dateTableColumns.push({
      title: displayDate,
      dataIndex: dateKey,
      key: dateKey,
      align: 'center'
    });
  }
  return { dateKeys, dateTableColumns };
};

interface GenerateFlightPredictionColumnsAndKeysParams {
  numberOfDays?: number;
  t: TFunction;
}
export const generateFlightPredictionColumnsAndKeys = <T extends Record<string, string | number> = DynamicTrapRow>({
  numberOfDays = 6,
  t
}: GenerateFlightPredictionColumnsAndKeysParams): { dateKeys: string[]; dateTableColumns: ColumnsType<T> } => {
  const anchorDate = getCurrentCalendarDate();
  const dateKeys: string[] = [];
  const dateTableColumns: ColumnsType<T> = [];

  const anchorDateKey = anchorDate.format('YYYY-MM-DD');
  dateKeys.push(anchorDateKey);
  dateTableColumns.push({
    title: formatMonthDay(anchorDate),
    dataIndex: anchorDateKey,
    key: anchorDateKey,
    align: 'center',
    width: 102
  });

  for (let i = 1; i < numberOfDays; i++) {
    const date = anchorDate.add(i, 'day');
    const dateKey = date.format('YYYY-MM-DD');
    const displayDate = formatMonthDay(date);

    dateKeys.push(dateKey);
    dateTableColumns.push({
      title: displayDate,
      dataIndex: dateKey,
      key: dateKey,
      align: 'center',
      width: 102
    });
  }

  return { dateKeys, dateTableColumns };
};

export const renderSevenDayTotalCell = (value: number, record: DynamicTrapRow) => {
  return (
    <StatusTag
      tagValue={value}
      type={StatusTypeEnum.SEVEN_DAY_TOTAL}
      status={record.status}
      tagPadding='0 6px'
      tagColor={COLOR_OPTIONS.NEUTRAL_100}
    />
  );
};

export const convertToNumber = (value: string | number): number => {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? -1 : numberValue;
};

export const mapTrapFeatureToDynamicRow = (
  trap: Feature<Point, RegionalOverviewDigitalTrapsProperties>,
  dateKeys: string[],
  t: TFunction
): DynamicTrapRow => {
  const trapData: DynamicTrapRow = {
    name: trap.properties.name,
    id: trap.properties.id,
    total: 0,
    status: trap.properties.level,
    statusMessage: t(`pages.regional_overview.digital_traps.${trap.properties.level}`),
    dailyInsectCounts: trap.properties.details.daily_insect_counts
  };

  const { mapped, trapCount, hasCountData } = mapDailyCountsKeys(trap.properties.details.daily_insect_counts, dateKeys);
  Object.assign(trapData, mapped);

  trapData.total = hasCountData ? trapCount : '--';

  const calculatedStatus = calculateRiskLevelFromThreshold(trapData.total, trap.properties.threshold);
  trapData.status = calculatedStatus;
  trapData.statusMessage = t(`pages.regional_overview.digital_traps.${calculatedStatus}`);

  return trapData;
};

export const groupAndTransformTraps = (trapGroups: TrapGroup[], dateKeys: string[], t: TFunction): TableTrapGroup[] => {
  return trapGroups.map(group => {
    const tableData: DynamicTrapRow[] = group.trapsInGroup.map(trap => mapTrapFeatureToDynamicRow(trap, dateKeys, t));

    return {
      title: group.title,
      key: group.id,
      tableDataSource: tableData
    };
  });
};

export const mergeDailyCounts = (traps: Feature<Point, RegionalOverviewDigitalTrapsProperties>[]): DailyInsectCounts[] => {
  const mergedCounts: Record<string, DailyInsectCounts> = {};

  traps.forEach(trap => {
    trap.properties.details.daily_insect_counts.forEach(count => {
      const dateKey = dayjs(count.date).format('YYYY-MM-DD');
      if (!mergedCounts[dateKey]) {
        mergedCounts[dateKey] = { date: count.date, total: 0 };
      }
      mergedCounts[dateKey].total += count.total;
    });
  });

  return Object.values(mergedCounts).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

interface CreateGraphLineParams {
  name: string;
  dailyCounts: DailyInsectCounts[];
  dateKeys: string[];
}
export const createGraphLine = ({ name, dailyCounts, dateKeys }: CreateGraphLineParams): SeriesLineOptions => {
  const { mapped } = mapDailyCountsKeys(dailyCounts, dateKeys);
  const result = Object.entries(mapped).map(([dateStr, value]) => ({
    x: new Date(dateStr).getTime(),
    y: value === '--' ? undefined : value
  })) as PointOptionsObject[];

  return {
    name,
    type: 'line',
    data: result
  };
};
